/**
 * Les cases a cocher d'effets, et leur survie au rechargement (#83 point 64).
 *
 * Sorti de `cockpit-firestore-store.service.ts`, qui pesait 1449 lignes et que
 * l'audit #83 nomme depuis un mois « trop gros pour etre raisonne ». Le cliquet
 * de taille l'a refuse au push le 2026-08-24: le fichier avait repris 58 lignes
 * depuis son gel, et c'est exactement ce que le cliquet existe pour empecher.
 *
 * DEPLACEMENT PUR. Aucune ligne de logique n'est reecrite ici — meme code, meme
 * ordre, meme commentaires. C'est deliberе: la suite de tests ne peut tourner ni
 * sur ce poste ni en CI aujourd'hui, et un decoupage qui en profite pour
 * "ameliorer au passage" est un decoupage qu'on ne peut plus relire par
 * comparaison.
 *
 * Le nom n'est pas invente non plus: `cockpit-effects-persistence.spec.ts`
 * existait deja a cote, et importait ces fonctions depuis le service. La spec
 * avait nomme le module avant qu'il existe.
 *
 * Rien d'Angular ici, rien d'injectable: du `localStorage` et des fonctions
 * pures.
 */

/**
 * Ce qu'une entree hydratee porte: les bascules d'effets, rien d'autre.
 *
 * Le service manipule un `OptimisticPatch` plus large (il porte aussi
 * `linkage`), mais ce module n'en a jamais lu que `effectsEnabled`. Declarer le
 * type etroit ici plutot que d'importer le large evite un cycle d'import entre
 * les deux fichiers, et dit ce que la persistance connait vraiment.
 */
export interface PersistedEffectToggles {
  effectsEnabled: Record<string, boolean>;
}

/* -------------------------------------------------------------------------- */
/* V2.7 — persisted per-card effect toggles (localStorage)                    */
/* -------------------------------------------------------------------------- */

const EFFECTS_ENABLED_PREFIX = 'cockpit.effectsEnabled.';

/**
 * How long a persisted effect-toggle map survives without being rewritten.
 *
 * The backstop, not the main purge — {@link prunePersistedEffectsEnabled} does
 * that on every load. This one covers the case no card-set comparison can: a
 * browser whose owner never opens the cockpit again. 30 days is well past the
 * lifetime of any card a checklist belongs to.
 */
const EFFECTS_ENABLED_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Stamped envelope written since audit #83 point 65. Bare maps are legacy. */
interface PersistedEffectsEntry {
  /** The effect-toggle map itself. */
  v: Record<string, boolean>;
  /** Epoch ms of the last write — drives {@link EFFECTS_ENABLED_TTL_MS}. */
  t: number;
}

/**
 * Read a stored value in either shape: the stamped envelope written today, or
 * the bare `{effectId: boolean}` map written before the TTL existed. A legacy
 * entry has no timestamp, so it cannot be aged out — the card-set prune below
 * is what removes it, on the very next load.
 */
function readPersistedEntry(raw: string): {map: Record<string, boolean>; at: number | null} | null {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  const envelope = parsed as Partial<PersistedEffectsEntry>;
  if (envelope.v && typeof envelope.v === 'object') {
    return { map: envelope.v, at: typeof envelope.t === 'number' ? envelope.t : null };
  }
  return { map: parsed as Record<string, boolean>, at: null };
}

/**
 * Read every persisted effect-toggle map back into the overlay shape, dropping
 * anything past its TTL on the way through.
 */
export function hydrateEffectsEnabled(now: number = Date.now()): Record<string, PersistedEffectToggles> {
  if (typeof localStorage === 'undefined') return {};
  const out: Record<string, PersistedEffectToggles> = {};
  const expired: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(EFFECTS_ENABLED_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const entry = readPersistedEntry(raw);
      if (!entry) continue;
      if (entry.at !== null && now - entry.at > EFFECTS_ENABLED_TTL_MS) {
        expired.push(key);
        continue;
      }
      out[key.slice(EFFECTS_ENABLED_PREFIX.length)] = { effectsEnabled: entry.map };
    }
    // Removal is deferred: mutating localStorage inside its own index loop
    // shifts every later index and silently skips entries.
    for (const key of expired) localStorage.removeItem(key);
  } catch {
    /* corrupt / unavailable storage — start clean */
  }
  return out;
}

/**
 * Drop every persisted checklist whose card is no longer in the ACTIVE working
 * set — audit #83 point 65.
 *
 * Only the approve path cleared these keys, so a card that was rejected,
 * snoozed, superseded, failed or handled externally left its entry behind for
 * ever, and `hydrateEffectsEnabled` swept the whole of localStorage at every
 * store construction: the sweep got slower with each abandoned card and never
 * shorter.
 *
 * "Not in the active set" is the right test rather than "status is terminal",
 * because the live listener queries `status not-in TERMINAL_CARD_STATUS_ARRAY`:
 * a card that resolves simply VANISHES from the stream, and there is no
 * terminal-transition event to hook. Absence is the event.
 *
 * Called with the first emission only — the working set is complete from the
 * first snapshot, and re-running it on every emission would fight the write in
 * `persistEffectsEnabled` for a card being edited right now.
 */
export function prunePersistedEffectsEnabled(activeCardIds: ReadonlySet<string>): number {
  if (typeof localStorage === 'undefined') return 0;
  const stale: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(EFFECTS_ENABLED_PREFIX)) continue;
      if (!activeCardIds.has(key.slice(EFFECTS_ENABLED_PREFIX.length))) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    return 0;
  }
  return stale.length;
}

export function persistEffectsEnabled(cardId: string, map: Record<string, boolean>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const entry: PersistedEffectsEntry = { v: map, t: Date.now() };
    localStorage.setItem(EFFECTS_ENABLED_PREFIX + cardId, JSON.stringify(entry));
  } catch {
    /* quota / unavailable — non-fatal */
  }
}

export function clearPersistedEffectsEnabled(cardId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(EFFECTS_ENABLED_PREFIX + cardId);
  } catch {
    /* non-fatal */
  }
}

